import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { m } from '~/paraglide/messages.js';

import type { TeamMemberData } from '../-schemas/onboarding-schema';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
];

type Props = {
  values: TeamMemberData[];
  onSubmit: (members: TeamMemberData[]) => void;
  onSkip: () => void;
};

export function InviteTeamComponent({ values, onSubmit, onSkip }: Props) {
  const [members, setMembers] = useState<TeamMemberData[]>(
    values.length > 0
      ? values
      : [{ id: crypto.randomUUID(), name: '', email: '', role: 'admin' }],
  );

  const addMember = () => {
    setMembers([
      ...members,
      { id: crypto.randomUUID(), name: '', email: '', role: '' },
    ]);
  };

  const removeMember = (id: string) => {
    if (members.length > 1) {
      setMembers(members.filter((member) => member.id !== id));
    }
  };

  const updateMember = (
    id: string,
    field: keyof TeamMemberData,
    value: string,
  ) => {
    setMembers(
      members.map((member) =>
        member.id === id ? { ...member, [field]: value } : member,
      ),
    );
  };

  const validMembers = members.filter((m) => m.name && m.email && m.role);

  const handleContinue = () => {
    onSubmit(validMembers.length > 0 ? validMembers : []);
  };

  return (
    <div className="w-full max-w-lg space-y-6">
      <p className="text-sm text-muted-foreground">
        {m['onboarding.invite_team.info']()}
      </p>

      <div className="space-y-3 rounded-xl border border-border p-4">
        <div className="hidden sm:grid sm:grid-cols-[1fr_1.4fr_120px_36px] sm:gap-3 sm:px-1 sm:pb-1">
          <span className="text-xs font-medium text-muted-foreground">
            {m['organization.add_team.name_label']()}
          </span>
          <span className="text-xs font-medium text-muted-foreground">
            {m['organization.add_team.email_label']()}
          </span>
          <span className="text-xs font-medium text-muted-foreground">
            {m['organization.add_team.role_label']()}
          </span>
          <span />
        </div>

        <div className="max-h-[320px] space-y-3 overflow-y-auto">
          {members.map((member) => (
            <div
              key={member.id}
              className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.4fr_120px_36px] sm:gap-3 sm:items-center"
            >
              <Input
                placeholder={m['organization.add_team.name_placeholder']()}
                value={member.name}
                onChange={(e) =>
                  updateMember(member.id, 'name', e.target.value)
                }
                className="h-10"
              />

              <Input
                type="email"
                placeholder={m['organization.add_team.email_placeholder']()}
                value={member.email}
                onChange={(e) =>
                  updateMember(member.id, 'email', e.target.value)
                }
                className="h-10"
              />

              <Select
                value={member.role}
                onValueChange={(value) =>
                  updateMember(member.id, 'role', value)
                }
              >
                <SelectTrigger className="h-10 w-full">
                  <SelectValue
                    placeholder={m['organization.add_team.role_placeholder']()}
                  />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <button
                type="button"
                onClick={() => removeMember(member.id)}
                disabled={members.length === 1}
                className="flex h-10 w-10 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-destructive hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={addMember}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-sm font-medium text-foreground transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <Plus className="h-4 w-4" />
          {m['organization.add_team.add_member']()}
        </button>

        {validMembers.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {validMembers.length}{' '}
            {validMembers.length === 1 ? 'member' : 'members'}
          </span>
        )}
      </div>

      <Button
        onClick={handleContinue}
        className="flex w-full items-center justify-center gap-2 py-6 font-semibold transition-all"
      >
        {m['common.continue']()}
        <ArrowRight className="h-5 w-5" />
      </Button>

      <button
        type="button"
        onClick={onSkip}
        className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        {m['onboarding.invite_team.skip']()}
      </button>
    </div>
  );
}
